/**
 * Sector operations — Construction Member DPR Review section (R39 B9.b2).
 * Mobile-first DPR review queue for phone width (375 px).
 * Online-only actions with server-computed permissions flags.
 */

import React, { useEffect, useMemo, useState, useId } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog, Button, Select, StatusPill, TextInput } from '../../Common'
import type { SelectOption } from '../../Common'
import { listConProjects } from '../../../services/conProjectsService'
import {
  listDprs,
  getDpr,
  reviewDpr,
  returnDpr,
  approveDpr,
} from '../../../services/conDprService'
import { getRecordPhotoUrl } from '../../../services/opsRecordPhotosService'
import { createOpId } from '../../../services/helper/opId'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import type {
  ConProject,
  OpsRecord,
  ConDprBlock,
  OpsRecordCorrection,
} from '../../../types/ops'
import styles from './MemberDprPhoneSection.module.css'

export interface MemberDprPhoneSectionProps {
  workspaceId: string
  callerUid?: string
  scope: {
    ready: boolean
    restricted: boolean
    allowedPlaceIds?: ReadonlySet<string>
  }
}

const unwrapVal = (v: unknown): string =>
  v != null && typeof v === 'object' && 'target' in v
    ? String((v as { target: { value?: unknown } }).target.value ?? '')
    : String(v ?? '')

export function MemberDprPhoneSection(props: MemberDprPhoneSectionProps): React.ReactElement {
  const { workspaceId, scope } = props
  const { t } = useNls()
  const reviewNotesId = useId()
  const returnReasonId = useId()
  const approveNotesId = useId()

  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Projects
  const [projects, setProjects] = useState<ConProject[]>([])
  const [userSelectedProjectId, setUserSelectedProjectId] = useState<string>('')
  const [projectsLoading, setProjectsLoading] = useState<boolean>(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId || !scope.ready) return
    let active = true

    async function loadProjects() {
      setProjectsLoading(true)
      try {
        const res = await listConProjects({ workspaceId })
        if (active) {
          setProjects(res.projects || [])
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setProjectsLoading(false)
      }
    }

    void loadProjects()
    return () => {
      active = false
    }
  }, [workspaceId, scope.ready])

  const readableProjects = useMemo(() => {
    if (!scope.restricted || !scope.allowedPlaceIds) return projects
    return projects.filter((p) => scope.allowedPlaceIds?.has(p.placeId))
  }, [projects, scope.restricted, scope.allowedPlaceIds])

  const effectiveProjectId = useMemo(() => {
    if (userSelectedProjectId && readableProjects.some((p) => p.id === userSelectedProjectId)) {
      return userSelectedProjectId
    }
    return readableProjects[0]?.id || ''
  }, [readableProjects, userSelectedProjectId])

  // DPR List
  const [dprList, setDprList] = useState<Array<OpsRecord & { dpr?: ConDprBlock }>>([])
  const [listLoading, setListLoading] = useState<boolean>(false)
  const [refreshNonce, setRefreshNonce] = useState<number>(0)

  useEffect(() => {
    if (!workspaceId || !effectiveProjectId) {
      return
    }

    let active = true
    void Promise.resolve().then(async () => {
      if (!active) return
      setListLoading(true)
      setErrorMsg(null)
      try {
        const res = await listDprs({ workspaceId, projectId: effectiveProjectId })
        if (active) {
          const filtered = (res.records || []).filter(
            (r) => r.state === 'SUBMITTED' || r.state === 'REVIEWED',
          )
          setDprList(filtered)
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setListLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, effectiveProjectId, refreshNonce])

  // Selected DPR Detail
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState<boolean>(false)
  const [detailRecord, setDetailRecord] = useState<OpsRecord | null>(null)
  const [detailDpr, setDetailDpr] = useState<ConDprBlock | null>(null)
  const [detailCorrections, setDetailCorrections] = useState<OpsRecordCorrection[]>([])
  const [detailFlags, setDetailFlags] = useState<{
    canReview: boolean
    canReturn: boolean
    canApprove: boolean
  }>({ canReview: false, canReturn: false, canApprove: false })

  // Photo URLs map
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!workspaceId || !selectedRecordId) {
      return
    }

    let active = true
    void Promise.resolve().then(async () => {
      if (!active) return
      setDetailLoading(true)
      setErrorMsg(null)
      setDetailFlags({ canReview: false, canReturn: false, canApprove: false })
      setDetailRecord(null)
      setDetailDpr(null)
      try {
        const res = await getDpr({ workspaceId, recordId: selectedRecordId })
        if (active) {
          setDetailRecord(res.record)
          setDetailDpr(res.dpr)
          setDetailCorrections(res.corrections || [])
          setDetailFlags({
            canReview: Boolean(res.canReview),
            canReturn: Boolean(res.canReturn),
            canApprove: Boolean(res.canApprove),
          })

          // Resolve photo paths
          const paths: string[] = []
          for (const l of res.dpr?.lines || []) {
            if (Array.isArray(l.photoPaths)) {
              for (const p of l.photoPaths) {
                if (p) paths.push(p)
              }
            }
          }
          if (paths.length > 0) {
            void Promise.all(
              paths.map(async (path) => {
                try {
                  const url = await getRecordPhotoUrl(path)
                  if (active) {
                    setPhotoUrls((prev) => ({ ...prev, [path]: url }))
                  }
                } catch {
                  // Ignore photo load failure
                }
              }),
            )
          }
        }
      } catch (err) {
        if (active) {
          setErrorMsg(getApiErrorMessage(err))
          setDetailFlags({ canReview: false, canReturn: false, canApprove: false })
          setDetailRecord(null)
          setDetailDpr(null)
        }
      } finally {
        if (active) setDetailLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, selectedRecordId, refreshNonce])

  // Dialogs
  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false)
  const [reviewNotes, setReviewNotes] = useState<string>('')
  const [returnDialogOpen, setReturnDialogOpen] = useState<boolean>(false)
  const [returnReason, setReturnReason] = useState<string>('')
  const [approveDialogOpen, setApproveDialogOpen] = useState<boolean>(false)
  const [approveNotes, setApproveNotes] = useState<string>('')
  const [actionBusy, setActionBusy] = useState<boolean>(false)

  const handleReviewSubmit = async () => {
    if (!selectedRecordId || !isOnline) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await reviewDpr({
        workspaceId,
        recordId: selectedRecordId,
        notes: reviewNotes.trim() || null,
        opId: createOpId('rev'),
      })
      setReviewDialogOpen(false)
      setReviewNotes('')
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleReturnSubmit = async () => {
    if (!selectedRecordId || returnReason.trim().length < 3 || !isOnline) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await returnDpr({
        workspaceId,
        recordId: selectedRecordId,
        reason: returnReason.trim(),
        opId: createOpId('ret'),
      })
      setReturnDialogOpen(false)
      setReturnReason('')
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const handleApproveSubmit = async () => {
    if (!selectedRecordId || !isOnline) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      await approveDpr({
        workspaceId,
        recordId: selectedRecordId,
        notes: approveNotes.trim() || null,
        opId: createOpId('app'),
      })
      setApproveDialogOpen(false)
      setApproveNotes('')
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  const projectOptions: SelectOption<string>[] = useMemo(() => {
    return readableProjects.map((p) => ({
      value: p.id,
      label: `${p.code} — ${p.name}`,
    }))
  }, [readableProjects])

  return (
    <section
      className={styles.section}
      data-testid="member-dpr-section"
      aria-labelledby="member-dpr-title"
    >
      <div className={styles.sectionHeader}>
        <div className={styles.titleGroup}>
          <h2 id="member-dpr-title" className={styles.sectionTitle}>
            {t('NLSMemberOpsDprTitle')}
          </h2>
          <p className={styles.sectionDesc}>
            {t('NLSMemberOpsDprSubtitle')}
          </p>
        </div>
      </div>

      {!isOnline && (
        <div className={styles.offlineBanner} role="status">
          {t('NLSMemberOpsOfflineNotice')}
        </div>
      )}

      {errorMsg && (
        <div className={styles.errorBanner} role="alert">
          {errorMsg}
        </div>
      )}

      {/* Project Picker */}
      <div className={styles.pickerRow} data-testid="member-dpr-project-select">
        <span className={styles.pickerLabel}>
          {t('NLSMemberOpsDprProjectPickerLabel')}
        </span>
        <Select
          ariaLabel={t('NLSMemberOpsDprProjectPickerLabel')}
          value={effectiveProjectId}
          options={projectOptions}
          onChange={(val) => {
            setUserSelectedProjectId(unwrapVal(val))
            setSelectedRecordId(null)
          }}
          disabled={projectsLoading || readableProjects.length === 0}
        />
      </div>

      {readableProjects.length === 0 && !projectsLoading && (
        <div className={styles.emptyState}>
          {t('NLSMemberOpsDprEmptyQueue')}
        </div>
      )}

      {/* DPR List View */}
      {!selectedRecordId && readableProjects.length > 0 && (
        <div className={styles.listContainer}>
          {listLoading ? (
            <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
          ) : dprList.length === 0 ? (
            <div className={styles.emptyState}>
              {t('NLSMemberOpsDprEmptyQueue')}
            </div>
          ) : (
            dprList.map((record) => (
              <div
                key={record.id}
                className={styles.dprCard}
                onClick={() => setSelectedRecordId(record.id || null)}
                data-testid={`member-dpr-card-${record.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedRecordId(record.id || null)
                  }
                }}
              >
                <div className={styles.dprCardHeader}>
                  <span className={styles.dateShift}>
                    {record.dpr?.reportDate || '—'}
                  </span>
                  <StatusPill tone="neutral">
                    {record.state || 'SUBMITTED'}
                  </StatusPill>
                </div>
                <div className={styles.dprCardMeta}>
                  <span>{record.dpr?.shift || 'DAY'}</span>
                  <span>{record.dpr?.totals?.lines ?? 0} lines</span>
                  <span>{record.dpr?.totals?.headcount ?? 0} headcount</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* DPR Detail View */}
      {selectedRecordId && (
        <div className={styles.listContainer}>
          <div className={styles.detailHeader}>
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedRecordId(null)
              }}
              data-testid="member-dpr-back-btn"
            >
              Back
            </Button>
            {detailRecord?.state && (
              <StatusPill tone="info">
                {detailRecord.state}
              </StatusPill>
            )}
          </div>

          {detailLoading ? (
            <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
          ) : detailDpr ? (
            <>
              {/* Card Overview */}
              <div className={styles.cardOverview}>
                <div className={styles.overviewRow}>
                  <span>Date:</span>
                  <strong>{detailDpr.reportDate}</strong>
                </div>
                <div className={styles.overviewRow}>
                  <span>Shift:</span>
                  <strong>{detailDpr.shift}</strong>
                </div>
                <div className={styles.overviewRow}>
                  <span>Total headcount:</span>
                  <strong>{detailDpr.totals?.headcount || 0}</strong>
                </div>
              </div>

              {/* Production Lines */}
              <div className={styles.subSection}>
                <h3 className={styles.subTitle}>{t('NLSMemberOpsDprLinesTitle')}</h3>
                <div className={styles.linesList}>
                  {(detailDpr.lines || []).map((line, idx) => (
                    <div key={line.lineId || idx} className={styles.lineCard}>
                      <div className={styles.lineTitleRow}>
                        <span>{line.activity}</span>
                        <span>
                          {line.doneQty} {line.unit}
                          {line.plannedQty != null ? ` / ${line.plannedQty}` : ''}
                        </span>
                      </div>
                      <div className={styles.lineMetaRow}>
                        <span>Trade: {line.trade}</span>
                      </div>
                      {line.gangs && line.gangs.length > 0 && (
                        <div className={styles.gangsRow}>
                          Gangs: {line.gangs.map((g) => `${g.trade} (${g.headcount})`).join(', ')}
                        </div>
                      )}
                      {line.photoPaths && line.photoPaths.length > 0 && (
                        <div className={styles.photosRow}>
                          {line.photoPaths.map((p) => (
                            photoUrls[p] ? (
                              <img
                                key={p}
                                src={photoUrls[p]}
                                alt="line attachment"
                                className={styles.photoThumb}
                              />
                            ) : null
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Amendments / Corrections */}
              {detailCorrections.length > 0 && (
                <div className={styles.subSection}>
                  <h3 className={styles.subTitle}>{t('NLSMemberOpsDprCorrectionsTitle')}</h3>
                  <div className={styles.correctionsList}>
                    {detailCorrections.map((c) => (
                      <div key={c.id} className={styles.correctionItem}>
                        <strong>{c.authorName || c.authorUid}:</strong> {c.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons: following server flags */}
              <div className={styles.actionButtons}>
                <div className={styles.actionRow}>
                  {detailFlags.canReview && (
                    <Button
                      variant="primary"
                      onClick={() => setReviewDialogOpen(true)}
                      disabled={!isOnline || actionBusy}
                      data-testid="member-dpr-btn-review"
                    >
                      {t('NLSMemberOpsDprReviewButton')}
                    </Button>
                  )}
                  {detailFlags.canReturn && (
                    <Button
                      variant="secondary"
                      onClick={() => setReturnDialogOpen(true)}
                      disabled={!isOnline || actionBusy}
                      data-testid="member-dpr-btn-return"
                    >
                      {t('NLSMemberOpsDprReturnButton')}
                    </Button>
                  )}
                  {detailFlags.canApprove && (
                    <Button
                      variant="primary"
                      onClick={() => setApproveDialogOpen(true)}
                      disabled={!isOnline || actionBusy}
                      data-testid="member-dpr-btn-approve"
                    >
                      {t('NLSMemberOpsDprApproveButton')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Review Dialog */}
      <AppDialog
        open={reviewDialogOpen}
        title={t('NLSMemberOpsDprReviewButton')}
        onDismiss={() => setReviewDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setReviewDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSMemberOpsDprConfirmReview'),
            variant: 'primary',
            onClick: handleReviewSubmit,
            disabled: actionBusy || !isOnline,
            testId: 'member-dpr-btn-confirm-review',
          },
        ]}
      >
        <div className={styles.dialogForm}>
          <div className={styles.dialogField}>
            <label htmlFor={reviewNotesId} className={styles.dialogLabel}>
              {t('NLSMemberOpsDprReviewNotesLabel')}
            </label>
            <TextInput
              id={reviewNotesId}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(unwrapVal(e))}
              placeholder={t('NLSMemberOpsDprReviewNotesPlaceholder')}
            />
          </div>
        </div>
      </AppDialog>

      {/* Return Dialog */}
      <AppDialog
        open={returnDialogOpen}
        title={t('NLSMemberOpsDprReturnButton')}
        onDismiss={() => setReturnDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setReturnDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSMemberOpsDprConfirmReturn'),
            variant: 'secondary',
            onClick: handleReturnSubmit,
            disabled: actionBusy || returnReason.trim().length < 3 || !isOnline,
            testId: 'member-dpr-btn-confirm-return',
          },
        ]}
      >
        <div className={styles.dialogForm}>
          <div className={styles.dialogField}>
            <label htmlFor={returnReasonId} className={styles.dialogLabel}>
              {t('NLSMemberOpsDprReturnReasonLabel')}
            </label>
            <TextInput
              id={returnReasonId}
              data-testid="member-dpr-return-reason-input"
              value={returnReason}
              onChange={(e) => setReturnReason(unwrapVal(e))}
              placeholder={t('NLSMemberOpsDprReturnReasonPlaceholder')}
            />
          </div>
        </div>
      </AppDialog>

      {/* Approve Dialog */}
      <AppDialog
        open={approveDialogOpen}
        title={t('NLSMemberOpsDprApproveButton')}
        onDismiss={() => setApproveDialogOpen(false)}
        actions={[
          {
            label: t('NLSCommonActionCancel'),
            variant: 'secondary',
            onClick: () => setApproveDialogOpen(false),
            disabled: actionBusy,
          },
          {
            label: t('NLSMemberOpsDprConfirmApprove'),
            variant: 'primary',
            onClick: handleApproveSubmit,
            disabled: actionBusy || !isOnline,
            testId: 'member-dpr-btn-confirm-approve',
          },
        ]}
      >
        <div className={styles.dialogForm}>
          <div className={styles.dialogField}>
            <label htmlFor={approveNotesId} className={styles.dialogLabel}>
              {t('NLSMemberOpsDprApproveNotesLabel')}
            </label>
            <TextInput
              id={approveNotesId}
              value={approveNotes}
              onChange={(e) => setApproveNotes(unwrapVal(e))}
              placeholder={t('NLSMemberOpsDprApproveNotesPlaceholder')}
            />
          </div>
        </div>
      </AppDialog>
    </section>
  )
}
